import type { ReactNode } from "react"

import type { ManualClPoolStore } from "@/domains/thick-v1/manual-cl-store"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/tooltip"
import { BodyXs } from "@/domains/design/components/body-xs"
import { cn, dollarsPrettified } from "@/domains/library"
import { AprBadge } from "@/domains/thick-v1/components/apr-badge"
import { RangeAprTooltipBody } from "@/domains/thick-v1/components/range-apr-tooltip-body"

/**
 * Uniswap-style metric row that sits below the header. Renders TVL, 24h
 * volume, 24h fees, Pool APR, 24h TVL delta, and 24h price change. Every
 * number lives on `ManualClPoolStore` as a computed — this component is a
 * pure renderer.
 */
export function PoolMetricRowView({ pool }: { pool: ManualClPoolStore }) {
  const rangeAprResult = pool.rangeApr7d
  const fullRangeApr = pool.displayApr
  return (
    <TooltipProvider>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <MetricTile label="TVL" value={dollarsPrettified(pool.tvlUsd)} />
        <MetricTile label="24h Volume" value={dollarsPrettified(pool.volumeUsd24h)} />
        <MetricTile label="24h Fees" value={dollarsPrettified(pool.feesUsd24h)} />
        <MetricTile
          label="7D range APR"
          value={
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex cursor-help">
                  <AprBadge value={rangeAprResult.formattedPct} variant="pool" />
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-xs">
                <RangeAprTooltipBody rangeAprResult={rangeAprResult} fullRangeApr={fullRangeApr} />
              </TooltipContent>
            </Tooltip>
          }
        />
        <MetricTile
          label="TVL 24h"
          value={formatSignedPct(pool.tvlUsdDelta24hPct)}
          tone={tone(pool.tvlUsdDelta24hPct)}
        />
        <MetricTile
          label="Price 24h"
          value={formatSignedPct(pool.priceChangePct24h)}
          tone={tone(pool.priceChangePct24h)}
        />
      </div>
    </TooltipProvider>
  )
}

function MetricTile({
  label,
  value,
  tone = "neutral",
}: {
  label: string
  value: ReactNode
  tone?: "neutral" | "up" | "down"
}) {
  const content =
    typeof value === "string" || typeof value === "number" ? (
      <span
        className={cn(
          "mt-1 text-base font-medium",
          tone === "up" && "text-primary-400",
          tone === "down" && "text-error-text",
          tone === "neutral" && "text-t100",
        )}
      >
        {value}
      </span>
    ) : (
      <div className="mt-1">{value}</div>
    )
  return (
    <div className="flex flex-col rounded-lg border border-p400-light bg-p400 p-4">
      <BodyXs className="uppercase tracking-wide text-t300">{label}</BodyXs>
      {content}
    </div>
  )
}

function formatSignedPct(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return "—"
  const sign = value >= 0 ? "+" : ""
  return `${sign}${value.toFixed(2)}%`
}

function tone(value: number | undefined): "neutral" | "up" | "down" {
  if (value === undefined || !Number.isFinite(value)) return "neutral"
  return value >= 0 ? "up" : "down"
}
